import { useEffect, useMemo, useRef, useState, useContext } from 'react';
import { Play, Users, Clock, Percent, Activity, Monitor, Smartphone } from 'react-feather';
import { useHistory, useLocation } from 'react-router';
import {
    LineChart, Line, AreaChart, Area, CartesianGrid, XAxis, YAxis,
    Tooltip as RechartsTooltip, ResponsiveContainer, Legend,
} from 'recharts';
import moment from 'moment';
import { useRemote } from '../../../Utils/Utils';
import { BannerContext } from '../../../InternalApiApp';
import { analyticsExportFilename } from '../AnalyticsUtils';
import Page, { PageContentBodyGrid } from '../../Shared/Page/Page';
import PageGridItem from '../../Shared/Page/PageGridItem';
import PageGridDivider from '../../Shared/Page/PageGridDivider';
import FlexWrapper from '../../FlexWrapper';
import LoadingIcon from '../../Shared/LoadingIcon';
import Table from '../../../components/Table/Table';
import Tooltip from '../../../components/popups/Tooltip';
import AnalyticsMetricFilter from '../../../components/Analytics/AnalyticsMetricFilter';
import AnalyticsSourceSelector from '../AnalyticsSourceSelector';
import AnalyticsExportButton from '../Components/AnalyticsExportButton';
import DateRangeInput from '../../CraigsLibraryPage/DateRangeInput';
import '../Charts/RechartsOverrides.scss';
import './AnalyticsVideoPage.scss';
import {
    parseRangeFromSearch, defaultRange, buildRangeSearch,
    secondsToMmSs, formatPercent, formatUploadedDate,
    playsTooltip, uniqueViewersTooltip,
} from './videoAnalyticsUtils';

// Analytics → Videos → drill-down (CFS-96): date-ranged engagement for one Bunny video —
// plays & watch-time daily series, audience retention (Bunny heatmap) and country split.
const AnalyticsVideoPage = ({ datatita }) => {
    const { setBannerBreadCrumbs, setBannerAdditionalContent, setBacNoDivider } = useContext(BannerContext);
    const remote = useRemote();
    const history = useHistory();
    const location = useLocation();
    const videoId = datatita.match.params.videoId;

    const urlRange = parseRangeFromSearch(location.search);
    const initialRange = urlRange.from && urlRange.to ? urlRange : defaultRange();

    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(false);
    const [range, setRange] = useState(initialRange);

    const rangeRef = useRef(range);
    rangeRef.current = range;
    const locationRef = useRef(location);
    locationRef.current = location;

    useEffect(() => {
        const handleRangeChange = ({ from, to }) => {
            const nextFrom = from ? from.format("YYYY-MM-DD") : null;
            const nextTo = to ? to.format("YYYY-MM-DD") : null;
            if (!nextFrom || !nextTo) return;
            if (rangeRef.current?.from === nextFrom && rangeRef.current?.to === nextTo) return;
            setRange({ from: nextFrom, to: nextTo });
            history.replace(locationRef.current.pathname + buildRangeSearch(locationRef.current.search, nextFrom, nextTo));
        };

        setBacNoDivider(true);
        setBannerAdditionalContent(
            <div style={{ display: "flex", alignItems: "center", justifyContent: "right", marginLeft: "auto", gap: "1rem" }}>
                <DateRangeInput
                    onChange={handleRangeChange}
                    value={{ from: moment(initialRange.from), to: moment(initialRange.to) }}
                />
                <AnalyticsSourceSelector />
            </div>
        );
        return () => {
            setBacNoDivider(false);
            setBannerAdditionalContent(null);
        };
    }, []);

    useEffect(() => {
        let stale = false; // out-of-order responses must not clobber the latest request's state
        setLoading(true);
        setError(false);
        const query = range?.from && range?.to ? `?from=${range.from}&to=${range.to}` : "";
        remote.get(`analytics/video-metrics/${videoId}${query}`).then((response) => {
            if (stale) return;
            setDetail(response);
            setBannerBreadCrumbs({ tab: 'Videos', param1: { name: response.displayName, id: videoId } });
            setLoading(false);
        }).catch(() => {
            if (stale) return;
            setError(true);
            setLoading(false);
        });
        return () => { stale = true; };
    }, [remote, videoId, range]);

    const daily = useMemo(() => (detail?.daily || []).map(d => ({
        name: d.date,
        plays: d.plays,
        watchTimeSeconds: d.watchTimeSeconds,
    })), [detail]);

    const retention = detail?.retention;
    const countries = detail?.countries || [];
    const devices = detail?.devices || [];
    const deviceTotal = devices.reduce((acc, d) => acc + (d.viewers || 0), 0);

    const exportDate = range ? { from: range.from, to: range.to } : null;
    const dailyExportHeaders = useMemo(() => [
        { label: 'Date', key: 'name' },
        { label: 'Plays', key: 'plays' },
        { label: 'Watch Time (seconds)', key: 'watchTimeSeconds' },
    ], []);
    const retentionExportHeaders = useMemo(() => [
        { label: 'Position (%)', key: 'positionPercent' },
        { label: 'Audience (%)', key: 'value' },
    ], []);
    const countriesExportHeaders = useMemo(() => [
        { label: 'Country', key: 'country' },
        { label: 'Plays', key: 'plays' },
        { label: 'Watch Time (seconds)', key: 'watchTimeSeconds' },
    ], []);
    const devicesExportHeaders = useMemo(() => [
        { label: 'Device', key: 'device' },
        { label: 'Unique Viewers', key: 'viewers' },
    ], []);

    if (error) {
        return (
            <Page noBanner showScrollbar>
                <PageContentBodyGrid showScrollbar>
                    <PageGridItem colSpan="12">
                        <div style={{ paddingTop: "2rem" }}>Video not found.</div>
                    </PageGridItem>
                </PageContentBodyGrid>
            </Page>
        );
    }

    if (loading || !detail) {
        return (
            <Page noBanner showScrollbar>
                <PageContentBodyGrid showScrollbar>
                    <PageGridItem colSpan="12">
                        <LoadingIcon centered />
                    </PageGridItem>
                </PageContentBodyGrid>
            </Page>
        );
    }

    const dateTick = (value) => moment(value).format("D/M");

    return (
        <Page noBanner showScrollbar>
            <PageContentBodyGrid showScrollbar>
                <PageGridItem colSpan="12">
                    <FlexWrapper gap="xs" direction="column" align="flex-start">
                        <h2 style={{ margin: 0 }}>{detail.displayName}</h2>
                        <div className="colour-text-neutral-dark-40" style={{ fontSize: "0.85rem" }}>
                            {secondsToMmSs(detail.lengthSeconds)} · Uploaded {formatUploadedDate(detail.dateUploaded)}
                            {detail.displayName !== detail.bunnyTitle && <> · {detail.bunnyTitle}</>}
                        </div>
                    </FlexWrapper>
                </PageGridItem>
                <PageGridItem colSpan="12">
                    <FlexWrapper gap="xl">
                        <Tooltip maxWidth={300} label={playsTooltip(true, detail.from)}>
                            <AnalyticsMetricFilter label="Plays" icon={<Play />} count={detail.plays} pointer />
                        </Tooltip>
                        <Tooltip maxWidth={300} label={uniqueViewersTooltip(detail.capturedFrom)}>
                            <AnalyticsMetricFilter label="Unique Viewers" icon={<Users />} count={detail.uniqueViewers ?? 0} pointer />
                        </Tooltip>
                        <AnalyticsMetricFilter label="Avg Watch Time" icon={<Clock />} count={secondsToMmSs(detail.avgWatchTimeSeconds)} pointer />
                        <AnalyticsMetricFilter label="% Watched" icon={<Percent />} count={formatPercent(detail.percentWatched)} pointer />
                        <AnalyticsMetricFilter label="Engagement Score" icon={<Activity />} count={detail.engagementScore == null ? "—" : detail.engagementScore} pointer />
                    </FlexWrapper>
                </PageGridItem>
                <PageGridDivider colSpan="12" />
                <PageGridItem colSpan="12">
                    <div className="video-analytics-panels">
                        <div className="video-analytics-panel">
                            <FlexWrapper fullWidth justify="space-between" align="center">
                                <div className="table-title">Plays &amp; Watch Time</div>
                                <AnalyticsExportButton
                                    headers={dailyExportHeaders}
                                    data={daily}
                                    filename={analyticsExportFilename(`video-${videoId}-daily`, exportDate)}
                                    loading={loading}
                                />
                            </FlexWrapper>
                            <ResponsiveContainer width="100%" height={280}>
                                <LineChart data={daily} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                    <XAxis dataKey="name" tickLine={false} axisLine={false} tickFormatter={dateTick} className="small-axis-label" />
                                    <YAxis yAxisId="plays" axisLine={false} tickLine={false} allowDecimals={false} width={40} className="small-axis-label" />
                                    <YAxis yAxisId="watch" orientation="right" axisLine={false} tickLine={false} width={50} className="small-axis-label" />
                                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                                    <RechartsTooltip
                                        labelFormatter={(value) => moment(value).format("Do MMMM YYYY")}
                                        formatter={(value, name) => name === "Watch Time" ? [secondsToMmSs(value), name] : [value, name]}
                                    />
                                    <Line yAxisId="plays" type="monotone" dataKey="plays" strokeWidth={2} stroke="#0E8EFD" dot={false} name="Plays" />
                                    <Line yAxisId="watch" type="monotone" dataKey="watchTimeSeconds" strokeWidth={2} stroke="#62d9b7" dot={false} name="Watch Time" />
                                    <Legend align="left" iconType="plainline" wrapperStyle={{ paddingTop: '5px', fontSize: '0.85rem' }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                        <div className="video-analytics-panel">
                            <FlexWrapper fullWidth justify="space-between" align="center">
                                <div className="table-title">Audience Retention</div>
                                <AnalyticsExportButton
                                    headers={retentionExportHeaders}
                                    data={retention || []}
                                    filename={analyticsExportFilename(`video-${videoId}-retention`, exportDate)}
                                    loading={loading}
                                />
                            </FlexWrapper>
                            {retention ?
                                <ResponsiveContainer width="100%" height={280}>
                                    <AreaChart data={retention} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                        <XAxis dataKey="positionPercent" tickLine={false} axisLine={false} tickFormatter={(v) => `${Math.round(v)}%`} className="small-axis-label" />
                                        <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} width={45} className="small-axis-label" />
                                        <CartesianGrid vertical={false} strokeDasharray="3 3" />
                                        <RechartsTooltip
                                            labelFormatter={(value) => `${Math.round(value)}% through the video`}
                                            formatter={(value) => [`${value}%`, "Audience"]}
                                        />
                                        <Area type="monotone" dataKey="value" strokeWidth={2} stroke="#0E8EFD" fill="#0E8EFD" fillOpacity={0.15} name="Audience" />
                                    </AreaChart>
                                </ResponsiveContainer>
                                :
                                <div className="video-analytics-empty">Not enough data yet</div>
                            }
                        </div>
                        <div className="video-analytics-panel">
                            <FlexWrapper fullWidth justify="space-between" align="center">
                                <div className="table-title">Top Countries</div>
                                <AnalyticsExportButton
                                    headers={countriesExportHeaders}
                                    data={countries}
                                    filename={analyticsExportFilename(`video-${videoId}-countries`, exportDate)}
                                    loading={loading}
                                />
                            </FlexWrapper>
                            {countries.length > 0 ?
                                <Table
                                    minWidth={300}
                                    colWidths={["auto", "120px", "160px"]}
                                    headers={[
                                        { text: "Country" },
                                        { className: "text-align-right", text: "Plays" },
                                        { className: "text-align-right", text: "Watch Time" },
                                    ]}
                                    dataMap={countries.map((c, idx) => (
                                        <tr key={c.country || idx}>
                                            <td>{c.country}</td>
                                            <td><div className="text-align-right">{c.plays}</div></td>
                                            <td><div className="text-align-right">{secondsToMmSs(c.watchTimeSeconds)}</div></td>
                                        </tr>
                                    ))}
                                    noResults={{ message: "No country data" }}
                                />
                                :
                                <div className="video-analytics-empty">No country data yet</div>
                            }
                        </div>
                        <div className="video-analytics-panel">
                            <FlexWrapper fullWidth justify="space-between" align="center">
                                <div className="table-title">Device Usage</div>
                                <AnalyticsExportButton
                                    headers={devicesExportHeaders}
                                    data={devices}
                                    filename={analyticsExportFilename(`video-${videoId}-devices`, exportDate)}
                                    loading={loading}
                                />
                            </FlexWrapper>
                            {deviceTotal > 0 ?
                                <div className="video-analytics-devices">
                                    {devices.map((d) => (
                                        <div key={d.device} className="video-analytics-device-row">
                                            <span className="video-analytics-device-label">
                                                {d.device === 'desktop' ? <Monitor size={16} /> : d.device === 'mobile' ? <Smartphone size={16} /> : null}
                                                {d.device}
                                            </span>
                                            <div className="video-analytics-device-bar-track">
                                                <div className="video-analytics-device-bar" style={{ width: `${Math.round(d.viewers * 100 / deviceTotal)}%` }} />
                                            </div>
                                            <span className="video-analytics-device-count">{d.viewers} ({Math.round(d.viewers * 100 / deviceTotal)}%)</span>
                                        </div>
                                    ))}
                                </div>
                                :
                                <div className="video-analytics-empty">No viewer data yet</div>
                            }
                        </div>
                    </div>
                </PageGridItem>
            </PageContentBodyGrid>
        </Page>
    );
};

export default AnalyticsVideoPage;
